import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Communication, General } from 'icons/untitledui';

import { EmailComposeMenu } from 'shared/components/EmailComposeMenu/_index';
import { ReflexStep } from 'shiftNotes/_types';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface NoteCardComposeMessageProps {
  step: ReflexStep;
  noteId: string;
  reflexId: string;
}

const COPIED_FEEDBACK_MS = 2000;
const SUBJECT_PREFIX_REGEX = /^\s*subject\s*:\s*/i;

function extractSubjectAndBody(body: string): {
  subject: string;
  emailBody: string;
} {
  const trimmed = body.trim();
  if (!trimmed) return { subject: '', emailBody: '' };

  const lines = trimmed.split('\n');
  const firstLine = lines[0].trim();
  if (!SUBJECT_PREFIX_REGEX.test(firstLine)) {
    return { subject: '', emailBody: trimmed };
  }

  const subject = firstLine.replace(SUBJECT_PREFIX_REGEX, '').trim();
  const rest = lines.slice(1).join('\n').replace(/^\n+/, '');
  return { subject, emailBody: rest };
}

export default function NoteCardComposeMessage({
  step,
  noteId,
  reflexId,
}: NoteCardComposeMessageProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();

  const [justCopied, setJustCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    []
  );

  const messageBody = step.body ?? '';
  const { subject, emailBody } = extractSubjectAndBody(messageBody);
  const hasMessage = messageBody.trim().length > 0;

  if (!hasMessage) return null;

  const captureProperties = {
    shift_note_id: noteId,
    reflex_id: reflexId,
    step_id: step.id,
  };

  const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigator.clipboard.writeText(messageBody);
    shiftNotesCapture.noteReflexMessageCopied(captureProperties);
    setJustCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      setJustCopied(false);
    }, COPIED_FEEDBACK_MS);
  };

  return (
    <>
      <Styled.StepSectionLabel>
        {t('instantReflex.messageLabel')}
      </Styled.StepSectionLabel>
      <Styled.ComposeMessageBody>
        <Styled.ComposeMessageBodyText>
          {emailBody || messageBody}
        </Styled.ComposeMessageBodyText>
      </Styled.ComposeMessageBody>
      <Styled.StepActionRow>
        <Styled.StepActionPill
          type="button"
          onClick={handleCopy}
          aria-live="polite"
          aria-label={t('instantReflex.copy')}
        >
          <General.Copy03 />
          {justCopied ? t('instantReflex.copied') : t('instantReflex.copy')}
        </Styled.StepActionPill>
        <EmailComposeMenu
          payload={{ subject, body: emailBody }}
          onSelected={(provider) =>
            shiftNotesCapture.noteReflexMessageSent({
              ...captureProperties,
              provider,
            })
          }
          renderTrigger={({ toggle }) => (
            <Styled.StepActionPill
              type="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                toggle(e);
              }}
              aria-label={t('instantReflex.share')}
            >
              <Communication.Send01 />
              {t('instantReflex.share')}
            </Styled.StepActionPill>
          )}
        />
      </Styled.StepActionRow>
    </>
  );
}
